/**
 * Evolution FAMILIES — who shares a line with whom.
 *
 * The Dex grid's Line filter needs two answers the app did not have:
 *
 *   "whole line"  — given Electabuzz, also give me Elekid and Electivire.
 *   "base only"   — given a set, keep the ones nothing evolves into.
 *
 * SPECIES_EVOLUTIONS (speciesBaseStats.generated.ts, from PvPoke) is the app's
 * existing evolution graph: a species -> its direct evolutions. It is DIRECTED
 * and forward-only, so it answers "what does this become" (bestPotential,
 * stardustPlanner, recommendations all use it that way) but not "what line is
 * this in": walking forward from Electivire returns nothing at all. A family is
 * the CONNECTED COMPONENT of that graph read undirected, which is what this
 * module builds, once, at module load.
 *
 * WHY THERE IS A PATCH TABLE
 * PvPoke's map is GO-accurate about GO-only evolutions (Meltan -> Melmetal,
 * which PokeAPI does not model at all) but it is keyed by NATIONAL DEX ID, and
 * that loses every evolution that only a REGIONAL form has: Galarian Meowth ->
 * Perrserker, Hisuian Sneasel -> Sneasler, Paldean Wooper -> Clodsire and so
 * on. Those species end up as orphan singletons, so "whole line" on Meowth
 * would quietly omit Perrserker. It also carries one reversed edge
 * (1013 -> 1012; Poltchageist evolves into Sinistcha, not the other way).
 *
 * The patch is checked against the OTHER evolution source in the repo,
 * BASE_STAGE_DEX_IDS (PokeAPI chains, used by Trade): after patching, the base
 * set derived here agrees with that file everywhere except the GO-only
 * evolutions PokeAPI does not know about. evolutionFamily.test.ts pins that
 * agreement, so a future regeneration of either file that drifts fails loudly
 * instead of silently dropping a stage out of a line.
 */
import { SPECIES_EVOLUTIONS } from "./generated/speciesBaseStats.generated";
import { BABY_DEX_NUMBERS } from "./babyDexNumbers";
import { NATIONAL_DEX_TOTAL } from "./constants";

/**
 * Evolutions the national-id-keyed source cannot express, because only a
 * REGIONAL form of the parent has them. Each is `parent -> [evolutions]`.
 */
export const REGIONAL_EVOLUTION_EDGES: Readonly<Record<number, readonly number[]>> =
  Object.freeze({
    52: [863],   // Galarian Meowth   -> Perrserker
    83: [865],   // Galarian Farfetch'd -> Sirfetch'd
    122: [866],  // Galarian Mr. Mime -> Mr. Rime
    194: [980],  // Paldean Wooper    -> Clodsire
    211: [904],  // Hisuian Qwilfish  -> Overqwil
    215: [903],  // Hisuian Sneasel   -> Sneasler
    222: [864],  // Galarian Corsola  -> Cursola
    264: [862],  // Galarian Linoone  -> Obstagoon
    562: [867],  // Galarian Yamask   -> Runerigus
  });

/**
 * Plain missing / wrong edges in the source, unrelated to regional forms.
 *
 * Shedinja is the branch PvPoke drops (Nincada evolves into BOTH Ninjask and
 * Shedinja). Phione/Manaphy neither exists in Pokémon GO; the edge is here only
 * so this module's base set matches the PokeAPI-derived one and the test that
 * compares them stays exact.
 */
export const EVOLUTION_EDGE_ADDITIONS: Readonly<Record<number, readonly number[]>> =
  Object.freeze({
    290: [292],   // Nincada -> Shedinja (the branch beside Ninjask)
    489: [490],   // Phione -> Manaphy, per PokeAPI's chain rooting
    1012: [1013], // Poltchageist -> Sinistcha
  });

/** Edges the source has BACKWARDS. Removed before the additions are applied. */
export const EVOLUTION_EDGE_REMOVALS: Readonly<Record<number, readonly number[]>> =
  Object.freeze({
    1013: [1012], // Sinistcha does not evolve into Poltchageist
  });

/** species -> direct evolutions, source graph plus every correction above. */
function buildForwardEdges(): Map<number, number[]> {
  const out = new Map<number, number[]>();
  const add = (from: number, to: number) => {
    const removed = EVOLUTION_EDGE_REMOVALS[from];
    if (removed?.includes(to)) return;
    const list = out.get(from);
    if (list) {
      if (!list.includes(to)) list.push(to);
    } else {
      out.set(from, [to]);
    }
  };
  for (const [rawFrom, tos] of Object.entries(SPECIES_EVOLUTIONS)) {
    for (const to of tos) add(Number(rawFrom), to);
  }
  for (const table of [REGIONAL_EVOLUTION_EDGES, EVOLUTION_EDGE_ADDITIONS]) {
    for (const [rawFrom, tos] of Object.entries(table)) {
      for (const to of tos) add(Number(rawFrom), to);
    }
  }
  return out;
}

const FORWARD = buildForwardEdges();

/** Every id something evolves INTO — i.e. every non-base stage. */
const EVOLVED_INTO = (() => {
  const set = new Set<number>();
  for (const tos of FORWARD.values()) for (const to of tos) set.add(to);
  return set;
})();

/**
 * id -> its family, sorted by dex number, ALWAYS including the id itself. A
 * species with no evolutions and no pre-evolution is a family of one, so every
 * caller can treat the result uniformly instead of null-checking.
 */
const FAMILY_BY_ID = (() => {
  // Undirected adjacency, so a walk from any member reaches the whole line.
  const neighbours = new Map<number, number[]>();
  const link = (a: number, b: number) => {
    const list = neighbours.get(a);
    if (list) list.push(b);
    else neighbours.set(a, [b]);
  };
  for (const [from, tos] of FORWARD) {
    for (const to of tos) {
      link(from, to);
      link(to, from);
    }
  }

  const byId = new Map<number, readonly number[]>();
  const seen = new Set<number>();
  for (let id = 1; id <= NATIONAL_DEX_TOTAL; id += 1) {
    if (seen.has(id)) continue;
    // BFS the component containing `id`.
    const members: number[] = [];
    const queue = [id];
    seen.add(id);
    while (queue.length > 0) {
      const cur = queue.shift()!;
      members.push(cur);
      for (const next of neighbours.get(cur) ?? []) {
        if (seen.has(next)) continue;
        seen.add(next);
        queue.push(next);
      }
    }
    members.sort((a, b) => a - b);
    const frozen = Object.freeze(members);
    for (const m of members) byId.set(m, frozen);
  }
  return byId;
})();

/**
 * Every species sharing an evolution line with this one, including itself,
 * in dex order. An unknown or out-of-range id is a family of one.
 */
export function familyFor(dexId: number): readonly number[] {
  return FAMILY_BY_ID.get(dexId) ?? [dexId];
}

/** The region whose form owns each REGIONAL_EVOLUTION_EDGES entry: only that
 * form of the parent takes the edge (an Alolan Meowth does not become a
 * Perrserker, a Galarian one does). Keyed by the parent, like the edges. */
export const REGIONAL_EVOLUTION_REGION: Readonly<Record<number, "alola" | "galar" | "hisui" | "paldea">> =
  Object.freeze({
    52: "galar",
    83: "galar",
    122: "galar",
    194: "paldea",
    211: "hisui",
    215: "hisui",
    222: "galar",
    264: "galar",
    562: "galar",
  });

/**
 * What `dexId` evolves INTO, one step, in source order (the directed graph the
 * families above are built from). `region` names the regional form that alone
 * takes the edge (Galarian Meowth to Perrserker), null for an evolution every
 * form of the parent has.
 */
export function evolvesInto(dexId: number): ReadonlyArray<{ to: number; region: "alola" | "galar" | "hisui" | "paldea" | null }> {
  const tos = FORWARD.get(dexId);
  if (!tos) return [];
  const regional = REGIONAL_EVOLUTION_EDGES[dexId] ?? [];
  const plain = (SPECIES_EVOLUTIONS as Record<number, readonly number[] | undefined>)[dexId] ?? [];
  return tos.map((to) => ({
    to,
    region: regional.includes(to) && !plain.includes(to) ? REGIONAL_EVOLUTION_REGION[dexId] ?? null : null,
  }));
}

/** to -> the species that evolve into it (the forward edges, reversed). */
const BACKWARD = (() => {
  const out = new Map<number, number[]>();
  for (const [from, tos] of FORWARD) {
    for (const to of tos) {
      const list = out.get(to);
      if (list) list.push(from);
      else out.set(to, [from]);
    }
  }
  return out;
})();

/**
 * What `dexId` evolves FROM, one step back, in source order. `region` names
 * the regional form of the parent that alone takes the edge (Sneasler comes
 * from Hisuian Sneasel, Perrserker from Galarian Meowth), null when every
 * form of the parent evolves this way.
 */
export function evolvesFrom(dexId: number): ReadonlyArray<{ from: number; region: "alola" | "galar" | "hisui" | "paldea" | null }> {
  const froms = BACKWARD.get(dexId);
  if (!froms) return [];
  return froms.map((from) => {
    const regional = REGIONAL_EVOLUTION_EDGES[from] ?? [];
    const plain = (SPECIES_EVOLUTIONS as Record<number, readonly number[] | undefined>)[from] ?? [];
    return { from, region: regional.includes(dexId) && !plain.includes(dexId) ? REGIONAL_EVOLUTION_REGION[from] ?? null : null };
  });
}

/**
 * True when nothing evolves into this species: the top of a line, or a
 * single-stage species (which is the top of a line of one).
 */
export function isLineBase(dexId: number): boolean {
  return !EVOLVED_INTO.has(dexId);
}

/**
 * The species a BABY evolves into: Pikachu, Clefairy, Jigglypuff, Togetic,
 * the three Hitmons, Electabuzz, Marill, Roselia, Snorlax, Lucario …
 *
 * Derived from the same (patched) forward edges as everything else here, so a
 * corrected edge corrects this set too.
 */
const BABY_EVOLVED_INTO = (() => {
  const set = new Set<number>();
  for (const baby of BABY_DEX_NUMBERS) for (const to of FORWARD.get(baby) ?? []) set.add(to);
  return set;
})();

/** True when this species is what a baby Pokémon evolves into. */
export function isBabyEvolution(dexId: number): boolean {
  return BABY_EVOLVED_INTO.has(dexId);
}

/**
 * The "base" a trainer means (2026-09-09 owner ask): the top of a line, OR the
 * first evolution of a baby. Pikachu shipped two generations before Pichu,
 * Electabuzz before Elekid, Snorlax before Munchlax; trainers file those as
 * the start of the line and a "Base only" filter that hid Pikachu but showed
 * Pichu read as broken. Babies themselves stay base (they are the top of the
 * line in the strict sense), so a Base filter shows both Pichu and Pikachu.
 *
 * isLineBase keeps the strict meaning for the callers that need exactly one
 * species per line (a raid boss picker must not offer Pichu and Pikachu).
 */
export function isBaseOrBabyEvolution(dexId: number): boolean {
  return isLineBase(dexId) || BABY_EVOLVED_INTO.has(dexId);
}

/** The union of every input id's whole family. */
export function expandToFamilies(ids: Iterable<number>): Set<number> {
  const out = new Set<number>();
  for (const id of ids) for (const member of familyFor(id)) out.add(member);
  return out;
}
